import React, { useEffect, useMemo, useCallback } from "react";
import { Card, Table, Button, Typography, Tag, Spin, Alert, Row, Col, Select, Space } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { reducer, sliceKey, actions } from "./slice";
import { testingAnalyticsDetailedSaga } from "./saga";
import { selectTableData, selectLoading, selectError, selectPagination } from "./selectors";

const { Title, Text } = Typography;
const { Option } = Select;

const coloredText = (value, color) => (
  <span style={{ color, fontWeight: 500 }}>{value}</span>
);

const softTag = (value, color) => (
  <Tag
    style={{
      backgroundColor: "#fff",
      color,
      border: `1px solid ${color}`,
      padding: "0 10px",
      fontWeight: 500,
    }}
  >
    {value}
  </Tag>
);

const columns = [
  { title: "Lab Name", dataIndex: "labName", key: "labName" },
  {
    title: "Total Orders",
    dataIndex: "totalOrders",
    key: "totalOrders",
    render: (v) => coloredText(v, "#000"),
  },
  {
    title: "In-Transit",
    dataIndex: "inTransit",
    key: "inTransit",
    render: (v) => softTag(v, "#3f51b5"), // Indigo
  },
  {
    title: "Delivered to Lab",
    dataIndex: "deliveredToLab",
    key: "deliveredToLab",
    render: (v) => softTag(v, "#52c41a"), // Green
  },
  {
    title: "Accepted by Lab",
    dataIndex: "acceptedByLab",
    key: "acceptedByLab",
    render: (v) => softTag(v, "#9254de"), // Purple
  },
  {
    title: "Under Testing",
    dataIndex: "underTesting",
    key: "underTesting",
    render: (v) => softTag(v, "#faad14"), // Amber
  },
  {
    title: "Report Uploaded",
    dataIndex: "reportUploaded",
    key: "reportUploaded",
    render: (v) => softTag(v, "#13c2c2"), // Cyan
  },
  {
    title: "Rejected by Lab",
    dataIndex: "rejectedByLab",
    key: "rejectedByLab",
    render: (v) => softTag(v, "#f5222d"), // Red
  },
  {
    title: "Under Dispute",
    dataIndex: "underDispute",
    key: "underDispute",
    render: (v) => softTag(v, "#f74513ff"), 
  },
  {
    title: "Avg TAT (Report)",
    dataIndex: "avgTatReport",
    key: "avgTatReport",
    render: (v) => {
      const days = parseFloat(v);
      const color = days > 3 ? "red" : "black";
      return <Tag color={color}>{v}d</Tag>;
    },
  },
  {
    title: "Avg TAT (Accept)",
    dataIndex: "avgTatAccept",
    key: "avgTatAccept",
    render: (v) => <Tag color="black">{v}h</Tag>,
  },
  {
    title: "Review Pending",
    dataIndex: "reviewPending",
    key: "reviewPending",
    render: (v) => softTag(v, "#000"),
  },
  {
    title: "Completed",
    dataIndex: "completed",
    key: "completed",
    render: (v) => softTag(v, "#52c41a"), // Green
  },
];

const TestingAnalyticsDetailed = ({ filters = {} }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: testingAnalyticsDetailedSaga });

  const loading = useSelector(selectLoading);
  const data = useSelector(selectTableData);
  const error = useSelector(selectError);
  const pagination = useSelector(selectPagination);

  // Memoize filters to prevent infinite renders
  const memoizedFilters = useMemo(() => ({
    companyIds: filters.companyIds || [],
    projectCodeIds: filters.projectCodeIds || [],
    selectedState: filters.selectedState || [],
  }), [
    filters.companyIds,
    filters.projectCodeIds,
    filters.selectedState,
  ]);

  // Create stable callback for navigation
  const handleBackClick = useCallback(() => {
    history.push("/bo/dashboard", { filters: memoizedFilters });
  }, [history, memoizedFilters]);

  // Handle pagination changes
  const handleTableChange = useCallback((page, pageSize) => {
    dispatch(actions.setPagination({ currentPage: page, pageSize }));
  }, [dispatch]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    dispatch(actions.setPageSize(newPageSize));
  }, [dispatch]);

  // Fetch data when memoized filters change
  useEffect(() => {
    dispatch(
      actions.fetchDetailed({
        filtersString: JSON.stringify(memoizedFilters),
      })
    );
  }, [dispatch, memoizedFilters]);

  // Reset pagination when filters change
  useEffect(() => {
    dispatch(actions.resetPagination());
  }, [dispatch, memoizedFilters]);

  // Memoized table data
  const tableData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    return data.map((item, idx) => ({
      ...item,
      key: `${item.labName}-${idx}`,
      id: `${item.labName}-${idx}`,
    }));
  }, [data]);

  // Pagination configuration for Ant Design Table
  const paginationConfig = {
    current: pagination.currentPage,
    pageSize: pagination.pageSize,
    total: tableData.length,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => 
      `${range[0]}-${range[1]} of ${total} entries`,
    pageSizeOptions: ['10', '30', '50', '100'],
    onChange: handleTableChange,
    onShowSizeChange: handleTableChange,
    style: { marginTop: 16 }
  };

  return (
    <div>
      <Card
        style={{ marginTop: 24 }}
        bodyStyle={{ padding: 0, backgroundColor: "#10948c" }} // Blue background
      >
        <Row align="middle" style={{ padding: "16px 24px" }}>
          <Col>
            <ArrowLeftOutlined
              style={{
                fontSize: 18,
                color: "#fff",
                marginRight: 12,
                cursor: "pointer",
              }}
              onClick={handleBackClick}
            />
          </Col>
          <Col flex="auto">
            <Title level={4} style={{ color: "#fff", margin: 0 }}>
              Complete Testing Analytics
            </Title>
          </Col>
          <Col>
            <Space align="center">
              <Text style={{ color: "#fff", fontSize: 14 }}>Show:</Text>
              <Select
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                style={{ width: 80 }}
                size="small"
              >
                <Option value={10}>10</Option>
                <Option value={30}>30</Option>
                <Option value={50}>50</Option>
                <Option value={100}>100</Option>
              </Select>
              <Text style={{ color: "#fff", fontSize: 14 }}>entries</Text>
            </Space>
          </Col>
        </Row>

        {/* Main Content */}
        <div style={{ padding: 24, backgroundColor: "#fff" }}>
          {error && (
            <Alert
              message="Error loading data"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Summary Info */}
          {!loading && tableData.length > 0 && (
            <div style={{ marginBottom: 16, color: "#666" }}>
              <Text>
                Showing {Math.min(pagination.pageSize, tableData.length)} of {tableData.length} entries
                {memoizedFilters.selectedState?.length > 0 && 
                  ` (filtered by ${memoizedFilters.selectedState.length} state${memoizedFilters.selectedState.length > 1 ? 's' : ''})`
                }
              </Text>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "50px" }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                Loading testing analytics data...
              </div>
            </div>
          ) : (
            <div>
              <Title level={5}>Complete Lab Analytics</Title>
              <Table
                columns={columns}
                dataSource={tableData}
                pagination={paginationConfig}
                rowKey="id"
                bordered
                scroll={{ x: true }}
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default TestingAnalyticsDetailed;